<template>
  <div>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      max-width="330"
      transition="slide-x-reverse-transition"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          :title="userInfo.fullName"
          text
          v-bind="attrs"
          v-on="on"
          class="d-flex align-center"
        >
          <Ava
            ref="avaActivator"
            class="ava-activator mr-1"
            :fio="userInfo.fullName"
          />
          <span
            class="primary--text"
            style="
              max-width: 80px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              text-transform: uppercase;
              font-weight: 500;
            "
            >{{ shortName }}</span
          >
        </div>
      </template>
      <v-card class="cabinet-profile-card pa-4">
        <v-form :ref="formRef">
          <div class="d-flex align-center mt-n3 mb-2">
            <v-btn class="ml-n2" @click="menu = false" icon color="primary">
              <v-icon>keyboard_arrow_right</v-icon>
            </v-btn>
            <a
              v-if="!editable"
              @click.prevent="editable = true"
              class="ml-auto"
              >{{ $t("Редактировать") }}</a
            >
          </div>
          <table class="black--text">
            <tr>
              <td class="text--center">
                <Ava ref="ava" class="mx-auto" :fio="userInfo.fullName" />
              </td>
              <td>
                <span class="fullname">{{ userInfo.fullName }}</span>
              </td>
            </tr>
            <tr v-if="editable">
              <td style="text-align: center">
                <FileChooser
                  accept=".jpg, .jpeg, .png"
                  :maxSize="maxAvaSize"
                  @filesReady="handleFileUpload"
                  #default="{ handler }"
                >
                  <a
                    @click.prevent="handler"
                    style="font-size: 14px"
                    class="d-block mt-md-n2 mt-n3"
                    >{{ $t(hasAva ? "СМЕНИТЬ ФОТО" : "ДОБАВИТЬ ФОТО") }}</a
                  >
                </FileChooser>
              </td>
              <td></td>
            </tr>
            <tr v-else>
              <td style="min-width: 84.55px">{{ $t("Логин") }}</td>
              <td>{{ userInfo.username }}</td>
            </tr>
            <tr>
              <td>E-mail</td>
              <td>
                <v-text-field
                  v-model="userInfo.email"
                  :readonly="!editable"
                  dense
                  :rules="editable ? [rules.required, rules.email] : []"
                  type="email"
                />
              </td>
            </tr>
            <tr>
              <td>{{ $t("Телефон") }}</td>
              <td>
                <MaskPhoneField
                  v-model="userInfo.telephone"
                  :readonly="!editable"
                  dense
                />
              </td>
            </tr>
            <tr>
              <td>{{ $t("Адрес") }}</td>
              <td>
                <v-text-field
                  v-model="userInfo.address"
                  :readonly="!editable"
                  dense
                />
              </td>
            </tr>
          </table>

          <div class="d-flex">
            <template v-if="editable">
              <v-btn :disabled="loading" @click="reset" text class="ml-auto">{{
                $t("Отменить")
              }}</v-btn>
              <v-btn
                :loading="loading"
                @click="handleSubmit"
                text
                color="primary"
                class="ml-1"
                >{{ $t("Сохранить") }}</v-btn
              >
            </template>
            <v-btn
              v-else
              class="ml-auto"
              text
              color="red"
              @click="handleLogout"
              >{{ $t("Выйти") }}</v-btn
            >
          </div>
        </v-form>
      </v-card>
    </v-menu>
  </div>
</template>
<script lang="ts">
import { Component, Mixins, Watch } from "vue-property-decorator";
import userModule from "@/store/user.module";
import authModule from "@/store/auth.module";
import Ava from "./Ava.vue";
import { FCFile, UserInfo } from "@/types";
import { cloneObject } from "@/utils";
import MaskPhoneField from "@/components/ui/MaskPhoneField.vue";
import FileChooser from "@/components/ui/FileChooser.vue";
import FormValidator from "@/mixins/FormValidator";
import baseModule from "@/store/base.module";
@Component({
  name: "CabinetProfile",
  components: {
    Ava,
    MaskPhoneField,
    FileChooser,
  },
})
export default class extends Mixins(FormValidator) {
  menu = false;
  userInfo: UserInfo = {};
  editable = false;
  loading = false;

  avaToSave: File | null = null;

  maxAvaSize = 15;

  get hasAva() {
    return userModule.hasAva;
  }

  get shortName() {
    if (!this.userInfo.fullName) return "";
    const splited = this.userInfo.fullName.split(" ");
    return splited.length > 1 ? `${splited[0][0]}${splited[1][0]}` : splited[0];
  }

  @Watch("menu")
  handleClose() {
    if (this.menu) return;
    this.reset();
  }
  async handleLogout() {
    await authModule.logout();
  }
  resetUserInfo() {
    this.userInfo = cloneObject(userModule.userInfo || {});
    if (this.avaToSave) {
      (this.$refs.avaActivator as Ava).getAva();
      (this.$refs.ava as Ava).getAva();
    }
    this.avaToSave = null;
  }

  async handleSubmit() {
    try {
      if (!this.validateForm()) return;
      this.loading = true;

      if (!(await userModule.putUserInfo(this.userInfo))) return;

      if (this.avaToSave && !(await userModule.postUserImage(this.avaToSave)))
        return;

      baseModule.showMessage({
        message: "Успешно сохранено",
        color: "success",
      });
      this.reset();
    } finally {
      this.loading = false;
    }
  }

  reset() {
    this.editable = false;
    this.resetUserInfo();
  }

  async handleFileUpload(files: FCFile[]) {
    if (this.loading) return;
    if (!files.length) {
      baseModule.showMessage({
        message: this.$t("fileMax", [this.maxAvaSize]) as string,
        color: "error",
      });
      return;
    }
    this.avaToSave = files[0].data as File;
    const reader = new FileReader();
    reader.readAsDataURL(this.avaToSave);
    reader.onload = () => {
      (this.$refs.ava as Ava).imageSrc = reader.result as string;
    };
  }

  async created() {
    if (!authModule.loggedIn) return;
    await userModule.getUserInfo();
    this.resetUserInfo();
  }
}
</script>
<style lang="scss">
@import "~vuetify/src/styles/styles.sass";
.cabinet-profile-card {
  .fullname {
    font-size: 20px;
    color: black;
  }
  table td {
    padding: 5px;
  }
}
.ava-activator {
  @include ava(40px);
  font-size: 30px;
}
</style>
